import React, { useState, useEffect } from 'react';
import InstagramHeader from '@Pages/AutomationBuilder/FlowBuilderComponents/InstagramHeader';
import ReactModal from 'react-modal';
import { useGetAllIGPostsMutation, useGetAllIGLiveMutation, useGetAllIGStoriesMutation } from '@Pages/AutomationBuilder/FlowBuilderComponents/StartNode/apiSlice';
import { RootState, useAppSelector } from '@Store/store';
import Loader from '@Components/Loader';
import { Switch } from '@Components/shadcn/ui/switch';
import { displayToast } from '@Utils/toast';
import posthog from 'posthog-js';
import { AnalyticsParamBuilder } from '@Utils/common';

interface TriggerStepProps {
    data: any;
    onUpdate: (data: any) => void;
    automationId?: number | string | null;
}


const COMPONENT = "trigger_step"

const getTriggerHeader = (triggerType: string) => {
    switch (triggerType) {
        case 'POSTREELCOMMENT':
            return 'User Comments on your post or reel';
        case 'STORYCOMMENT':
            return 'User replies to your stories';
        case 'LIVECOMMENT':
            return 'User comments on your live';
        case 'USERDM':
            return 'User DMs to you';
        default:
            return '';
    }
};

const defaultReplies = [
    "Got it, check your inbox!📬",
    "Great! Check your messages📩",
    "Sent :)",
    "Check your DM"
]

const TriggerStep: React.FC<TriggerStepProps> = ({ data, onUpdate, automationId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [mediaItems, setMediaItems] = useState<any[]>([]);

    const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
    const [currentKeyword, setCurrentKeyword] = useState('');
    const [keywords, setKeywords] = useState<string[]>(data.keywords || []);
    const [cooldown, setCooldown] = useState<number>(data.cooldown || 15);


    const { user } = useAppSelector((state: RootState) => state.profile);
    const selectedIgId = useAppSelector((state: RootState) => state.igSelection.selectedIgId);
    const activeIgId = selectedIgId || user?.ig_ids?.[0]?.igsid;

    const [getAllPosts, { isLoading: allIgPostIsLoading, error: allIgPostError }] = useGetAllIGPostsMutation();
    const [getAllStories, { isLoading: allIgStoriesLoading, error: allIgStoriesError }] = useGetAllIGStoriesMutation();
    const [getAllLive, { isLoading: allIgLiveLoading, error: allIgLiveError }] = useGetAllIGLiveMutation();

    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);




    // Add these new states
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [commentReplies, setCommentReplies] = useState<string[]>(data.commentReplies || defaultReplies);
    const [currentReply, setCurrentReply] = useState('');

    useEffect(() => {
        if (data?.commentReplies) setCommentReplies(data?.commentReplies);
        if (data?.keywords) setKeywords(data?.keywords);
        if (data?.selectedMedia) setMediaItems(data?.selectedMedia);
        if (data?.cooldown !== undefined) setCooldown(data?.cooldown);
    }, [data]);


    const handleCommentModalOpen = () => {
        setIsCommentModalOpen(true);
    };

    const handleCommentModalClose = () => {
        setIsCommentModalOpen(false);
        setCurrentReply('');
    };

    const handleReplyAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && currentReply.trim()) {
            setCommentReplies([...commentReplies, currentReply.trim()]);
            setCurrentReply('');
        }
    };

    const handleReplyDelete = (indexToDelete: number) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "delete_comment_reply",
            genus: data?.triggerType,
            str_value: commentReplies[indexToDelete],
        }));
        setCommentReplies(prev => prev.filter((_, index) => index !== indexToDelete));
    };

    const handleReplyConfirm = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "save_comment_reply",
            genus: data?.triggerType,
            str_value: commentReplies.join(';'),
        }));
        const filteredReplies = commentReplies.filter(reply => reply.trim() !== '');
        setCommentReplies([...filteredReplies])
        onUpdate({ ...data, commentReplies: filteredReplies });
        handleCommentModalClose();
    };



    const handleKeywordDelete = (indexToDelete: number) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "delete_keyword",
            genus: data?.triggerType,
            int_value: automationId,
            str_value: keywords[indexToDelete]
        }));
        setKeywords(prev => prev.filter((_, index) => index !== indexToDelete));
    };


    const OnSetupKeywordClick = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "setup_keyword",
            int_value: automationId,
        }));
        handleKeywordModalOpen()
    }

    const handleKeywordModalOpen = () => {
        posthog.capture("ui_view", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "setup_keyword",
            int_value: automationId,
        }));
        setIsKeywordModalOpen(true);
    };

    const handleKeywordModalClose = (from: string) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "setup_keyword",
            genus: from,
            int_value: automationId,
        }));
        posthog.capture("ui_close", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "setup_keyword",
            int_value: automationId,
        }));
        setIsKeywordModalOpen(false);
        setCurrentKeyword('');
    };

    const handleKeywordAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && currentKeyword.trim()) {
            posthog.capture("ui_action", AnalyticsParamBuilder({
                kingdom: COMPONENT,
                phylum: "modal",
                classValue: "add_keyword",
                genus: data?.triggerType,
                family: 'keypress',
                int_value: automationId,
                str_value: currentKeyword.trim()
            }));
            setKeywords([...keywords, currentKeyword.trim()]);
            setCurrentKeyword('');
        }
    };

    const handleKeywordConfirm = () => {
        let newKeywords = [...keywords]
        if (currentKeyword.trim()) {
            setKeywords(newKeywords);
            setCurrentKeyword('');
            newKeywords = [...keywords, currentKeyword.trim()]
        }
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "confirm_keywords",
            genus: data?.triggerType,
            int_value: automationId,
            str_value: currentKeyword
        }));
        onUpdate({ ...data, keywords: newKeywords, cooldown });
        handleKeywordModalClose('confirm');
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

        if (scrollPercentage >= 70 && !allIgPostIsLoading && hasMore) {
            fetchMedia(nextCursor);
        }
    };

    const fetchMedia = async (after?: string | null) => {
        if (!activeIgId) return;
        try {
            let response;
            const payload = {
                ig_id: activeIgId,
                limit: 15,
                ...(after && { after })
            };

            switch (data.triggerType) {
                case 'POSTREELCOMMENT':
                    response = await getAllPosts(payload).unwrap();
                    break;
                case 'STORYCOMMENT':
                    response = await getAllStories(payload).unwrap();
                    break;
                case 'LIVECOMMENT':
                    response = await getAllLive(payload).unwrap();
                    break;
                default:
                    return;
            }

            const newItems = response.data?.data || [];
            setMediaItems(prev => after ? [...prev, ...newItems] : newItems);

            // Update pagination state
            setNextCursor(response.data?.paging?.cursors?.after || null);
            setHasMore(!!response.data?.paging?.next);
        } catch (error: any) {
            // console.error('Error :', error.data.error);
            displayToast("error", error?.data?.error?.message || "Internal Error! please refresh the page")
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            // Reset pagination state when modal opens
            setNextCursor(null);
            setHasMore(true);
            setMediaItems([]);
            fetchMedia();
        }
    }, [isModalOpen]);


    const handleImageClick = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "select_media",
            classValue: data?.triggerType,
            int_value: automationId,
        }));
        posthog.capture("ui_view", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: data?.triggerType,
            genus: "select_media",
            int_value: automationId,
        }));
        setIsModalOpen(true);
    };

    const handleItemSelect = (item: any) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "select_media_item",
            genus: data?.triggerType,
            family: item?.id,
            str_value: item?.media_type,
            int_value: automationId
        }));
        setSelectedItem(item);
    };

    const handleConfirm = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "confirm_selected_media",
            genus: data?.triggerType,
            family: selectedItem?.id,
            str_value: selectedItem?.media_type,
            int_value: automationId
        }));
        onUpdate({ ...data, selectedMedia: selectedItem });
        setIsModalOpen(false);
        setSelectedItem(null);
    };

    // const handleTriggerSelect = (triggerType: string) => {
    //     onUpdate({ triggerType, imageData: null });
    // };

    const handleDelete = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "deselect_trigger",
            int_value: automationId,
        }));
        setKeywords([])
        setCommentReplies(defaultReplies)
        onUpdate({ triggerType: null });
    };

    const renderSelectedMedia = () => {
        if (!data.selectedMedia) return null;

        if (data.triggerType == "LIVECOMMENT") return (
            <div className="mt-2 p-3 border border-black rounded-lg">
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <p className="text-sm text-black line-clamp-2">
                            Current IG Live
                        </p>
                    </div>
                </div>
            </div>
        );

        return (
            <div className="mt-2 p-3 border rounded-lg">
                {data.selectedMedia?.id == "anyPost" ? <p className='font-bold'>All Posts</p> :
                    data.selectedMedia?.id == "anyStory" ? <p className='font-bold'>All Stories</p> :
                        <div className="flex items-center gap-3">
                            <div className="w-20 h-50 rounded-lg overflow-hidden">
                                <img
                                    src={data.selectedMedia.thumbnail_url || data.selectedMedia.media_url}
                                    alt="Selected media"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-600 line-clamp-2">
                                    {data.selectedMedia.caption || 'No caption'}
                                </p>
                            </div>
                        </div>}
            </div>
        );
    };


    const handleTriggerSelect = (triggerType: string) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "select_trigger",
            classValue: triggerType,
            int_value: automationId,
        }));
        if (triggerType === 'USERDM') {
            // For DMs, we only need keywords, no media selection
            onUpdate({ triggerType, channel_ig_id: activeIgId });
            handleKeywordModalOpen();
        } else {
            onUpdate({ triggerType, imageData: null });
        }
    };

    const handleAnyPost = () => {
        const item = { id: 'anyPost', channel_ig_id: activeIgId, media_type: 'anyPost' }
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "select_media_item",
            genus: data?.triggerType,
            family: item?.id,
            str_value: item?.media_type,
            int_value: automationId
        }));
        handleItemSelect(item)
    }

    const handleAnyStory = () => {
        const item = { id: 'anyStory', channel_ig_id: activeIgId, media_type: 'anyStory' }
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "select_media_item",
            genus: data?.triggerType,
            family: item?.id,
            str_value: item?.media_type,
            int_value: automationId
        }));
        handleItemSelect(item)
    }

    const renderContent = () => {
        if (data.triggerType) {
            return (
                <div className="space-y-6">
                    <div className="space-y-2">
                        <div className="relative border-[1px] p-2 rounded-lg hover:border-[#0084ff] my-2">
                            <button
                                onClick={handleDelete}
                                className="absolute top-1/2 -translate-y-1/2 right-3 p-2 hover:bg-gray-100 rounded-full"
                            >
                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <InstagramHeader header={getTriggerHeader(data.triggerType)} image_size="25px" header_text_weight="text-sm font-medium" />
                        </div>
                    </div>

                    {/* Only show media selection for non-DM triggers */}
                    {data.triggerType !== 'USERDM' && (
                        <div className="space-y-2">
                            <h3 className="text-sm font-medium text-gray-700">Which Post or Reel do you want to use?</h3>
                            {data.selectedMedia ? (
                                <div className="relative">
                                    {renderSelectedMedia()}
                                    <button
                                        onClick={handleImageClick}
                                        className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-full"
                                    >
                                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleImageClick}
                                    className="w-full p-4 border border-dashed rounded-lg hover:border-[#0084ff] transition-colors flex flex-col items-center justify-center gap-2"
                                >
                                    <div className="p-3 bg-gray-50 rounded-full">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-500">Select Post or Reel</span>
                                </button>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-700">
                            {data.triggerType === 'USERDM' ? 'What keywords in DMs will trigger your automation?' : 'What keywords will start your automation?'}
                        </h3>
                        {data.keywords?.length > 0 ? (
                            <div className="relative p-3 border rounded-lg">
                                <div className="flex flex-wrap gap-2">
                                    {!keywords.includes('*') && data.keywords.map((keyword: string, index: number) => (
                                        <span key={index} className="px-3 py-1 bg-[#7373ff] text-white rounded-full text-sm">
                                            {keyword}
                                        </span>
                                    ))}
                                    {keywords.includes('*') && <p className='font-bold'>Any Keyword</p>}
                                </div>
                                {data.triggerType === 'USERDM' && keywords.includes('*') && (
                                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <p className="text-xs text-amber-700 leading-relaxed mb-2">
                                            <b>Note</b>: Any keyword automations has a <b>{cooldown} minute cooldown</b>. Once triggered, they cannot trigger again util the timer resets. All other automation will still function for the user except any keyword in the DM.
                                        </p>
                                    </div>
                                )}
                                <button
                                    onClick={handleKeywordModalOpen}
                                    className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-full"
                                >
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={OnSetupKeywordClick}
                                className="w-full p-4 border border-dashed rounded-lg hover:border-[#0084ff] transition-colors flex flex-col items-center justify-center gap-2"
                            >
                                <div className="p-3 bg-gray-50 rounded-full">
                                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                    </svg>
                                </div>
                                <span className="text-sm text-gray-500">Setup Keywords</span>
                            </button>
                        )}
                    </div>

                    {data.triggerType == 'POSTREELCOMMENT' && <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-700">What do you want to reply to those comments?</h3>
                        {data.commentReplies?.length > 0 ? (
                            <div className="relative p-3 border rounded-lg">
                                <div className="flex flex-col gap-2">
                                    {data.commentReplies.map((reply: string, index: number) => (
                                        <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                            <div className="w-2 h-2 rounded-full bg-gray-300 text-center"></div>
                                            <span className="flex-1">{reply}</span>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={handleCommentModalOpen}
                                    className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-full"
                                >
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleCommentModalOpen}
                                className="w-full p-4 border border-dashed rounded-lg hover:border-[#0084ff] transition-colors flex flex-col items-center justify-center gap-2"
                            >
                                <div className="p-3 bg-gray-50 rounded-full">
                                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </div>
                                <span className="text-sm text-gray-500">Setup Comment Replies</span>
                            </button>
                        )}
                    </div>}
                </div>
            );
        }

        return (
            <div className="space-y-2 mt-4">
                <h3 className='font-medium text-sm'>Select trigger type</h3>
                <button
                    onClick={() => handleTriggerSelect('POSTREELCOMMENT')}
                    className="w-full p-2 md:p-3 flex items-center gap-2 md:gap-3 border rounded-lg hover:border-[#0084ff] transition-colors"
                >
                    <InstagramHeader header="User Comments on your post or reel" image_size="25px" header_text_weight="text-sm font-medium" />
                </button>

                {/* Add User DMs trigger option */}
                <button
                    onClick={() => handleTriggerSelect('USERDM')}
                    className="w-full p-2 md:p-3 flex items-center gap-2 md:gap-3 border rounded-lg hover:border-[#0084ff] transition-colors"
                >
                    <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-[25px] h-[25px] flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium">User DMs to you</span>
                    </div>
                </button>

                <button
                    onClick={() => handleTriggerSelect('LIVECOMMENT')}
                    className="w-full p-2 md:p-3 flex items-center gap-2 md:gap-3 border rounded-lg hover:border-[#0084ff] transition-colors"
                >
                    <InstagramHeader header="User Comments on your LIVE" image_size="25px" header_text_weight="text-sm font-medium" />
                </button>

                <button
                    onClick={() => handleTriggerSelect('STORYCOMMENT')}
                    className="w-full p-2 md:p-3 flex items-center gap-2 md:gap-3 border rounded-lg hover:border-[#0084ff] transition-colors"
                >
                    <InstagramHeader header="User replies to your stories" image_size="25px" header_text_weight="text-sm font-medium" />
                </button>

                {/* Existing disabled options */}
                <div className="w-full p-2 md:p-3 flex items-center justify-between border rounded-lg bg-gray-50 cursor-not-allowed">
                    <div className="flex items-center gap-2 md:gap-3">
                        <InstagramHeader header="User mentions you in story" image_size="25px" header_text_weight="text-sm font-medium text-gray-500" />
                    </div>
                    <span className="px-2 py-1 text-xs bg-[#7373ff] text-white rounded-full">Coming Soon</span>
                </div>


            </div>
        );
    };



    const handleModalClose = (from: string) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: from,
            genus: data?.triggerType,
            int_value: automationId,
        }));
        posthog.capture("ui_close", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "select_media",
            genus: data?.triggerType,
            int_value: automationId,
        }));
        setIsModalOpen(false);
        setSelectedItem(null);
        setMediaItems([]);
    };


    const enableAllKeyword = (status: boolean) => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "any_keyword_switch",
            genus: data?.triggerType,
            int_value: automationId,
            str_value: status
        }));
        if (status) {
            setKeywords(['*'])
        } else {
            setKeywords([])
        }
    }

    const AddKeywordClick = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "add_keyword",
            genus: data?.triggerType,
            family: 'add_button_press',
            int_value: automationId,
            str_value: currentKeyword.trim()
        }));
        if (currentKeyword.trim()) {
            setKeywords([...keywords, currentKeyword.trim()]);
            setCurrentKeyword('');
        }
    }

    const OnClickAddCommentReply = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "modal",
            classValue: "add_comment_reply",
            genus: data?.triggerType,
            int_value: automationId,
        }));
        setCommentReplies([...commentReplies, ''])
    }

    return (
        <div className="bg-white rounded-[20px] p-4 md:p-6">
            <div className="flex flex-row items-center mb-3 md:mb-4">
                <div className="h-full">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-amber-500 rounded-lg flex items-center justify-center text-white">
                        <svg className="w-5 h-5 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                </div>
                <div className="ml-2 md:ml-3">
                    <div className="text-xs md:text-sm">Select a Trigger</div>
                    <div className="text-sm md:text-md font-bold">When to run automation</div>
                </div>
            </div>

            {renderContent()}

            <ReactModal
                isOpen={isModalOpen}
                onRequestClose={() => handleModalClose("close_request")}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
             bg-white rounded-xl w-full max-w-2xl h-[80vh] px-6 pb-6 overflow-hidden"
                overlayClassName="z-50 fixed inset-0 bg-black bg-opacity-50"
            >
                {/* Use full height container inside */}
                <div className="flex flex-col h-full">
                    {/* Sticky header stays at top */}
                    <div className="sticky top-0 bg-white z-10 pb-4 border-b">
                        <div className="flex justify-between items-center mt-6">
                            <h2 className="text-lg font-bold">Select Post or Reel</h2>
                            <div className="flex items-center gap-2">
                                {selectedItem && (
                                    <button
                                        onClick={handleConfirm}
                                        className="bg-[#7373ff] text-white px-4 py-2 rounded-lg hover:bg-[#3535d5] transition-colors"
                                    >
                                        Confirm
                                    </button>
                                )}
                                <button
                                    onClick={() => handleModalClose('cross_icon')}
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable content fills remaining height */}
                    <div className="grid grid-cols-3 gap-4 overflow-y-auto p-4 flex-1" onScroll={handleScroll}>
                        {(data.triggerType === 'POSTREELCOMMENT') && !(allIgPostIsLoading || allIgLiveLoading || allIgStoriesLoading) && !(allIgPostError || allIgLiveError || allIgStoriesError) && <div
                            onClick={handleAnyPost}
                            className={`relative aspect-square rounded-lg border-2 transition-all flex items-center justify-center ${selectedItem?.id === 'anyPost'
                                ? 'border-purple-600'
                                : 'border-black hover:border-gray-300'
                                }`}
                        >
                            <p className="text-center text-sm px-2">All Posts</p>
                            {selectedItem?.id === "anyPost" && (
                                <div className="absolute top-2 right-2 bg-[#7373ff] rounded-full p-1">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                        </div>}
                        {(data.triggerType === 'STORYCOMMENT') && !(allIgPostIsLoading || allIgLiveLoading || allIgStoriesLoading) && !(allIgPostError || allIgLiveError || allIgStoriesError) && <div
                            onClick={handleAnyStory}
                            className={`relative aspect-square rounded-lg border-2 transition-all flex items-center justify-center ${selectedItem?.id === 'anyStory'
                                ? 'border-purple-600'
                                : 'border-black hover:border-gray-300'
                                }`}
                        >
                            <p className="text-center text-sm px-2">All Stories</p>
                            {selectedItem?.id === "anyStory" && (
                                <div className="absolute top-2 right-2 bg-[#7373ff] rounded-full p-1">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                        </div>}
                        {mediaItems?.length > 0 ? (
                            mediaItems.map((item: any, index: number) => (
                                <div
                                    key={index}
                                    onClick={() => handleItemSelect(item)}
                                    className={`relative aspect-square rounded-lg border-2 transition-all flex items-center justify-center ${selectedItem?.id === item.id
                                        ? 'border-purple-600'
                                        : 'border-black hover:border-gray-300'
                                        }`}
                                >
                                    {(data.triggerType === 'POSTREELCOMMENT' || data.triggerType === 'STORYCOMMENT') && (
                                        <img
                                            src={item?.thumbnail_url || item?.media_url}
                                            alt={`Instagram media ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                    {data.triggerType === 'LIVECOMMENT' && (
                                        <p className="text-center text-sm px-2">Current Live Video</p>
                                    )}
                                    {selectedItem?.id === item.id && (
                                        <div className="absolute top-2 right-2 bg-[#7373ff] rounded-full p-1">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            !(allIgPostIsLoading || allIgLiveLoading || allIgStoriesLoading) && (
                                <div className="col-span-3 flex items-center justify-center py-12">
                                    <p className="text-lg text-gray-500">No posts found</p>
                                </div>
                            )
                        )}
                        {(allIgPostIsLoading || allIgLiveLoading || allIgStoriesLoading) && (
                            <div className="col-span-3 flex items-center justify-center py-4">
                                <Loader isLoading={true} />
                            </div>
                        )}
                    </div>
                </div>
            </ReactModal>


            <ReactModal
                isOpen={isKeywordModalOpen}
                onRequestClose={() => handleKeywordModalClose('request_close')}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl w-full max-w-md p-6"
                overlayClassName="z-50 fixed inset-0 bg-black bg-opacity-50"
            >
                <div className="flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-bold">Setup Keywords</h2>
                        <button
                            onClick={() => handleKeywordModalClose('cross_icon')}
                            className="p-2 hover:bg-gray-100 rounded-full"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <p className="text-sm text-gray-500 mb-4">Keywords are not case-sensitive, e.g., "Link" and "link" are recognized as the same.</p>

                    <div>
                        {!keywords.includes('*') && <div>
                            <div className="relative mb-2">
                                <input
                                    type="text"
                                    value={currentKeyword}
                                    onChange={(e) => setCurrentKeyword(e.target.value)}
                                    onKeyDown={handleKeywordAdd}
                                    disabled={keywords.includes('*')}
                                    placeholder="Type & Hit ↵ Enter to add Keyword"
                                    className="w-full px-4 py-2 border rounded-lg pr-10 focus:outline-none focus:border-purple-500"
                                />
                                {currentKeyword &&
                                    <button
                                        onClick={AddKeywordClick}
                                        className="bg-green-200 px-4 py-1 text-sm rounded-lg text-grey-500 absolute right-2 top-1/2 -translate-y-1/2 hover:text-gray-600"
                                    >
                                        Add →
                                    </button>
                                }
                            </div>

                        </div>}

                        <div className='my-2 flex justify-between'>
                            <p>Any keyword</p>
                            <Switch className="data-[state=checked]:bg-green-500 align-middle"
                                checked={keywords.includes('*')}
                                name="anykeyword"
                                onCheckedChange={(status) => enableAllKeyword(status)}
                            />
                        </div>

                        {data.triggerType === 'USERDM' && keywords.includes('*') && (
                            <div className="space-y-4 mb-2">
                                <div className="flex items-center justify-between my-2">
                                    <p className="text-sm font-medium">Cooldown Duration</p>
                                    <select
                                        value={cooldown}
                                        onChange={(e) => setCooldown(parseInt(e.target.value))}
                                        className="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    >
                                        {Array.from({ length: 61 }, (_, i) => (
                                            <option key={i} value={i}>
                                                {i} min
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <p className="text-xs text-amber-700 leading-relaxed">
                                        <b>Note</b>: This automations has a <b>{cooldown} minute cooldown</b>. Once triggered, they cannot trigger again util the timer resets. All other automation will still function for the user except any keyword in the DM.
                                    </p>
                                </div>
                            </div>
                        )}



                        {/* {keywords.length > 0 && data.triggerType === 'USERDM' && <p className="text-sm text-red-500">Only one keyword is allowed</p>} */}
                    </div>

                    {!keywords.includes('*') && keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                            {keywords.map((keyword, index) => (
                                <span key={index} className="px-3 py-1 bg-[#7373ff] text-white rounded-full text-sm flex items-center gap-1">
                                    {keyword}
                                    <button
                                        onClick={() => handleKeywordDelete(index)}
                                        className="ml-1 p-1 hover:bg-purple-200 rounded-full"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <button
                        onClick={handleKeywordConfirm}
                        className="w-full bg-[#7373ff] text-white py-3 rounded-lg  hover:bg-[#3535d5] transition-colors"
                    >
                        Confirm
                    </button>
                </div>
            </ReactModal>

            <ReactModal
                isOpen={isCommentModalOpen}
                onRequestClose={handleCommentModalClose}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl w-full max-w-md p-6"
                overlayClassName="z-50 fixed inset-0 bg-black bg-opacity-50"
            >
                <div className="flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-bold">Setup Comment Replies</h2>
                        <button
                            onClick={handleCommentModalClose}
                            className="p-2 hover:bg-gray-100 rounded-full"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <p className="text-sm text-gray-500 mb-4">Add Random Comment Replies</p>

                    {commentReplies.map((reply, index) => (
                        <div key={index} className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
                            <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                            <input
                                type="text"
                                value={reply}
                                onChange={(e) => {
                                    const newReplies = [...commentReplies];
                                    const newValue = e.target.value;
                                    if (newValue.trim() === '') {
                                        newReplies.splice(index, 1);
                                    } else {
                                        newReplies[index] = newValue;
                                    }
                                    setCommentReplies(newReplies);
                                }}
                                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
                            />
                            <button
                                onClick={() => handleReplyDelete(index)}
                                className="p-2 hover:bg-gray-100 rounded-full text-gray-500"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                    ))}

                    <button
                        onClick={OnClickAddCommentReply}
                        className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#3535d5] hover:text-[#3535d5] transition-colors mb-4"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Reply
                    </button>

                    <button
                        onClick={handleReplyConfirm}
                        className="w-full bg-[#7373ff] text-white py-3 rounded-lg hover:bg-[#3535d5]  transition-colors"
                    >
                        Confirm
                    </button>
                </div>
            </ReactModal>
        </div>
    );
};

export default TriggerStep;
